"use client";

import * as React from "react";
import { HoverCard as HoverCardPrimitive } from "radix-ui";

import { cn } from "../lib/cn";

/**
 * Read-only popover triggered by hover or focus.
 *
 * Use for previews (citation cards, `@user` mentions). NOT for
 * interactive content — users can't reliably click into a hover-only
 * surface. For clickable popovers, use `<Popover>` or `<DropdownMenu>`.
 */
function HoverCard({ ...props }: React.ComponentProps<typeof HoverCardPrimitive.Root>) {
  return <HoverCardPrimitive.Root data-slot="hover-card" {...props} />;
}

/**
 * Element that opens the card on hover/focus.
 */
function HoverCardTrigger({ ...props }: React.ComponentProps<typeof HoverCardPrimitive.Trigger>) {
  return <HoverCardPrimitive.Trigger data-slot="hover-card-trigger" {...props} />;
}

/**
 * The card body. Portaled and positioned relative to the trigger.
 *
 * The default treatment was reconciled with Unified's `UiHoverCard` during the
 * registry-audit reverse-sync (#219): a content-hugging width capped at 280px, a
 * `border-hairline` divider, tighter `p-3 text-sm` padding, and the wide, soft
 * `shadow-pop` lift (the audit's "hover shadow differs" finding). Override any of
 * it via `className`.
 */
function HoverCardContent({
  className,
  align = "center",
  sideOffset = 6,
  ...props
}: React.ComponentProps<typeof HoverCardPrimitive.Content>) {
  return (
    <HoverCardPrimitive.Portal data-slot="hover-card-portal">
      <HoverCardPrimitive.Content
        data-slot="hover-card-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-auto max-w-[280px] origin-(--radix-hover-card-content-transform-origin) rounded-md border border-hairline bg-popover p-3 text-sm text-popover-foreground shadow-pop outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      />
    </HoverCardPrimitive.Portal>
  );
}

export { HoverCard, HoverCardTrigger, HoverCardContent };
